import cx from 'clsx';
import { forwardRef, type ReactElement, type ReactNode, useReducer } from 'react';
import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult,
} from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Description } from '#components/form-elements/description/description';
import { Error } from '#components/form-elements/error/error';
import { Input } from '#components/form-elements/input/input';
import { FieldLabel } from '#components/form-elements/label/label';
import { EyeIcon } from './icons/eye';
import { EyeHiddenIcon } from './icons/eye-hidden';
import styles from './text-field.module.css';

interface TextFieldBaseProps extends AriaTextFieldProps {
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string) | ReactNode;
  placeholder: string;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
}

// Omitting the visible `label` requires `aria-label`/`aria-labelledby` so the
// input always keeps a programmatic accessible name (WCAG 4.1.2).
type TextFieldLabelProps =
  | { label: string | ReactNode }
  | { label?: undefined; 'aria-label': string }
  | { label?: undefined; 'aria-labelledby': string };

export type TextFieldComponentProps = TextFieldBaseProps & TextFieldLabelProps;

export const TextField = forwardRef<HTMLInputElement, TextFieldComponentProps>(function TextField(
  {
    label,
    description,
    errorMessage,
    placeholder,
    className,
    type,
    multiple,
    isOptional,
    isOptionalText,
    ...props
  },
  ref
): ReactElement {
  const { formatMessage } = useIntl();
  const [isPasswordVisible, togglePasswordVisibility] = useReducer(state => !state, false);

  const resolvedErrorMessage =
    errorMessage ??
    (props.isRequired
      ? ({ validationDetails }: ValidationResult) =>
          validationDetails.valueMissing ? formatMessage({ id: 'field-error-required' }) : ''
      : undefined);

  return (
    <AriaTextField
      className={cx(className, styles.container)}
      type={!isPasswordVisible ? type : 'text'}
      {...props}
    >
      {Boolean(label) && (
        <FieldLabel>
          {label}
          {Boolean(isOptional) && [
            ' ',
            isOptionalText || formatMessage({ id: 'form-elements-optional' }),
          ]}
        </FieldLabel>
      )}
      <span className={styles['input-container']}>
        <Input
          autoFocus={props.autoFocus}
          multiple={multiple}
          placeholder={placeholder}
          ref={ref}
        />

        {type === 'password' && (
          <button
            aria-label={
              isPasswordVisible
                ? formatMessage({ id: 'a11y-buttons-hide-password-aria-label' })
                : formatMessage({ id: 'a11y-buttons-show-password-aria-label' })
            }
            className={styles.icon}
            onClick={togglePasswordVisibility}
            type="button"
          >
            {isPasswordVisible ? <EyeHiddenIcon aria-hidden /> : <EyeIcon aria-hidden />}
          </button>
        )}
      </span>
      <Error>{resolvedErrorMessage}</Error>
      {Boolean(description) && <Description>{description}</Description>}
    </AriaTextField>
  );
});
